import type { LineItem } from "@commercetools/platform-sdk";
import { ProductType } from "@/common/course/types";
import type { Program } from "@/common/program/types";
import type { BundleInfoMap, CartData } from "@/utils/cartUtils";
import type { EnrichedCart } from "@/lib/ecommerce/commercetools/cart";
import { CheckoutQPs } from "@/lib/ecommerce/commercetools/client-only/constants";

const getBundleInfoMap = async (lineItems: LineItem[]) => {
  const bundleInfoMap: BundleInfoMap = {};

  const bundleIds = lineItems
    .map((lineItem: LineItem) => lineItem.custom?.fields?.bundleId)
    .filter((bundleId: string) => !!bundleId);
  const nonDuplicateBundleIds = [...new Set(bundleIds)];

  if (nonDuplicateBundleIds.length > 0) {
    try {
      const response = await fetch(`/api/programs`, {
        method: "POST",
        body: JSON.stringify({ uuids: nonDuplicateBundleIds }),
      });
      if (!response.ok) {
        console.error("Error fetching programs:", response.status);
        return {};
      }
      const programs = await response.json();
      programs?.forEach((program: Program) => {
        bundleInfoMap[program.externalId] = program;
      });
    } catch (e) {
      console.error("Error fetching programs:", e);
      return {};
    }
  }

  return bundleInfoMap;
};

// Convert CommerceTools Cart to our CartData format
const convertCartToCartData = async (cart: EnrichedCart): Promise<CartData> => {
  const bundleInfoMap = await getBundleInfoMap(cart.lineItems ?? []);
  const items =
    cart.lineItems?.map((item) => {
      // Check if this is a program by looking for bundleId in custom fields
      const bundleId = item.custom?.fields?.bundleId;
      const isProgram = !!bundleId;

      // Get enriched data for this item
      const courseUuid = item.variant?.attributes?.find(
        (attr) => attr.name === "course-uuid",
      )?.value as string;

      const enrichedKey = isProgram ? bundleId : `course-${courseUuid}`;
      const enrichedData = cart.enrichedProgramInfo?.[enrichedKey];
      const partnerLogo = enrichedData?.partners?.[0];

      return {
        id: item.id,
        courseKey:
          item.variant?.attributes?.find((attr) => attr.name === "course-key")
            ?.value ?? item.productKey,
        courseRunKey: item.variant?.key,
        courseUuid: courseUuid,
        courseName:
          item.name?.["en-US"] ||
          item.name?.en ||
          item.name?.[Object.keys(item.name || {})[0]] ||
          "Unknown Course",
        price: (item.price?.value?.centAmount || 0) / 100, // Convert cents to dollars
        quantity: item.quantity || 1,
        originalPrice: (item.price?.value?.centAmount || 0) / 100,
        totalPrice: (item.totalPrice?.centAmount || 0) / 100,
        type: isProgram
          ? (bundleInfoMap?.[bundleId]?.productType ?? ProductType.COURSE)
          : ProductType.COURSE,
        sku: item.variant?.sku,
        brand:
          (item.variant?.attributes?.find((attr) => attr.name === "brand-text")
            ?.value as string) ?? "Unknown Brand",
        image: partnerLogo?.logoUrl
          ? { url: partnerLogo.logoUrl }
          : item.variant?.images?.[0]?.url
            ? { url: item.variant.images[0].url }
            : undefined,
        bundleId: bundleId as string, // Include bundleId for programs
      };
    }) || [];

  const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0); // Sum of per-item prices (not considering quantity)
  const netTotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0); // Sum of line item totals (considering quantity)

  // Handle discounts if available
  const discounts =
    cart.discountCodes?.map((discount) => ({
      type: "fixed" as const,
      value: 0, // TODO: Calculate actual discount value from discountedPricePerQuantity
      description:
        discount.discountCode?.obj?.name?.["en-US"] ||
        discount.discountCode?.obj?.name?.en ||
        "Discount",
    })) || [];

  return {
    items,
    subtotal: subtotal, // Add subtotal back since CartData requires it
    discounts,
    total: netTotal,
    tax:
      (cart.taxedPrice?.totalGross?.centAmount || 0) / 100 -
      (cart.taxedPrice?.totalNet?.centAmount || 0) / 100,
    shipping: [(cart.shippingInfo?.price?.centAmount || 0) / 100],
    enrichedProgramInfo: cart.enrichedProgramInfo,
    bundleInfoMap,
  };
};

export const fetchCart = async (): Promise<CartData | null> => {
  try {
    const response = await fetch(`/api/ecommerce/cart/fetch-cart`, {
      method: "GET",
      credentials: "include", // Include Cookies
    });

    if (!response.ok) {
      // For 401 errors, return null instead of throwing
      // This allows anonymous users to still see their cart if they have one
      if (response.status === 401) {
        console.log("User not authenticated - checking for anonymous cart");
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Check if we have cart data in the response
    if (!data.cart) {
      console.log("[fetchCart Debug] No cart data in response");
      return null;
    }

    const cartData = await convertCartToCartData(data.cart);
    return cartData;
  } catch (error) {
    console.error("Error fetching cart:", error);
    throw error;
  }
};

export const addToCart = async (
  add: {
    courseRunKeys?: string[];
    productBundleIds?: string[];
  } = {},
): Promise<CartData | null> => {
  if (add.courseRunKeys?.length == 0 && add.productBundleIds?.length == 0) {
    return null;
  }

  let qps = {} as Record<CheckoutQPs, string[]>;

  if (add.productBundleIds?.length != 0 && add.productBundleIds) {
    qps[CheckoutQPs.Programs] = add.productBundleIds;
  }

  if (add.courseRunKeys?.length != 0 && add.courseRunKeys) {
    qps[CheckoutQPs.Courses] = add.courseRunKeys;
  }

  try {
    // Build the URL with query parameters manually since buildSafeUrl requires absolute URLs
    const url = new URL(
      "/api/ecommerce/cart/add-to-cart",
      window.location.origin,
    );
    // Add query parameters
    Object.entries(qps).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            url.searchParams.append(key, item);
          });
        } else {
          url.searchParams.append(key, value);
        }
      }
    });

    const response = await fetch(url.toString(), {
      method: "POST",
      credentials: "include", // Include Cookies
    });

    if (!response.ok) {
      // For 401 errors, return null instead of throwing
      // This allows anonymous users to still see their cart if they have one
      if (response.status === 401) {
        console.log("User not authenticated - checking for anonymous cart");
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Check if we have cart data in the response
    if (!data.cart) {
      return null;
    }

    return convertCartToCartData(data.cart);
  } catch (error) {
    console.error("Error fetching cart:", error);
    throw error;
  }
};
